/**
 * visualConfigs.js — Config des cartes visuelles HTML/CSS pour chaque thème AM.
 * Consommé par VisuelThemeCard via ThemesAM.jsx.
 */

export const VISUAL_CONFIGS = {
  'signalisation': {
    num: 1,
    title: 'La signalisation',
    subtitle: 'Comprendre et respecter les règles',
    central: {
      title: '3 grandes familles',
      items: [
        'Interdiction — rond rouge, ce que je ne peux pas faire',
        'Obligation — rond bleu, ce que je dois faire',
        'Danger — triangle rouge, ce qui va arriver',
      ],
    },
    pastilles: [
      { tone: 'success', desc: 'Je respecte STOP, Cédez-le-passage, feux.' },
      { tone: 'danger', desc: 'Je ne « devine » pas le sens des panneaux.' },
      { tone: 'warning', desc: 'Les panneaux organisent la circulation.' },
    ],
  },

  'priorites': {
    num: 3,
    title: 'Les priorités',
    subtitle: 'Identifier les règles aux intersections',
    central: {
      title: 'Sans panneau ? Priorité à DROITE',
      subtitle: 'Ralentis → observe → identifie la règle → décide',
    },
    pastilles: [
      { tone: 'success', desc: 'STOP = arrêt complet + pied au sol.' },
      { tone: 'danger', desc: 'Un doute = je m\'arrête et j\'observe.' },
      { tone: 'warning', desc: 'Je ne cède jamais sous la pression d\'un autre usager.' },
    ],
  },

  'distances-securite': {
    num: 4,
    title: 'Les distances de sécurité',
    subtitle: 'Pour avoir le temps de réagir',
    central: {
      big: '2 sec.',
      title: 'Règle des 2 secondes',
      subtitle: 'Un point fixe → « un-mille, deux-mille » après moi',
    },
    pastilles: [
      { tone: 'success', desc: 'Sous la pluie, je double la distance.' },
      { tone: 'danger', desc: 'Coller le véhicule devant = pas de temps de réaction.' },
      { tone: 'warning', desc: 'Plus je vais vite, plus la distance doit être grande.' },
    ],
  },

  'virages': {
    num: 6,
    title: 'Les virages',
    subtitle: 'Regarder, anticiper, se positionner',
    central: {
      title: 'Regarder → Ralentir → Placer → Sortir',
      subtitle: 'Mon regard emmène la trajectoire',
    },
    pastilles: [
      { tone: 'success', desc: 'Je ralentis AVANT le virage, pas dedans.' },
      { tone: 'danger', desc: 'Corrections brutales, trajectoire trop large.' },
      { tone: 'warning', desc: 'Je regarde vers la sortie, pas mon capot.' },
    ],
  },

  'changements-direction': {
    num: 7,
    title: 'Les changements de direction',
    subtitle: 'Tourner en sécurité',
    central: {
      title: 'Vérifie · Clignotant · Positionne · Tourne',
      subtitle: 'Le clignotant indique, il ne donne PAS la priorité',
    },
    pastilles: [
      { tone: 'success', desc: 'Je préviens, je me positionne, je contrôle.' },
      { tone: 'danger', desc: 'Tourner sans clignotant ou sans contrôle.' },
      { tone: 'warning', desc: 'Je vérifie mes usagers vulnérables avant de tourner.' },
    ],
  },

  'partage-route': {
    num: 8,
    title: 'Le partage de la route',
    subtitle: 'Être attentif aux autres usagers',
    central: {
      title: 'Piétons · Cyclistes · 2RM · Véhicules lourds',
      subtitle: 'Je respecte leur espace et j\'anticipe leurs réactions',
    },
    pastilles: [
      { tone: 'success', desc: 'Je laisse au moins 1 m aux cyclistes/piétons.' },
      { tone: 'danger', desc: 'Doubler trop près d\'un cycliste = danger.' },
      { tone: 'warning', desc: 'Un piéton peut changer de direction à tout moment.' },
    ],
  },

  'angles-morts': {
    num: 9,
    title: 'Les angles morts',
    subtitle: 'Ce que je ne vois pas peut me toucher',
    central: {
      title: 'Ne pas voir ≠ absence de danger',
      subtitle: 'Camions, bus → zones aveugles importantes',
    },
    pastilles: [
      { tone: 'success', desc: 'Je regarde par-dessus l\'épaule avant chaque manœuvre.' },
      { tone: 'danger', desc: 'Rester collé à un camion = tu es invisible.' },
      { tone: 'warning', desc: 'Si tu ne vois pas les yeux du chauffeur dans son rétro, il ne te voit pas.' },
    ],
  },

  'conditions-circulation': {
    num: 10,
    title: 'Les conditions de circulation',
    subtitle: 'S\'adapter à la météo et à la route',
    central: {
      title: 'Je vois moins → je ralentis → j\'augmente mes marges',
      subtitle: 'Pluie · brouillard · nuit · chaussée glissante',
    },
    pastilles: [
      { tone: 'success', desc: 'Feux de croisement dès que la visibilité baisse.' },
      { tone: 'danger', desc: 'Rouler pareil sous la pluie qu\'au sec.' },
      { tone: 'warning', desc: 'Les conditions changent vite : je reste attentif.' },
    ],
  },

  'entretien-vehicule': {
    num: 11,
    title: 'L\'entretien du véhicule',
    subtitle: 'Pour rouler en sécurité',
    central: {
      title: 'Pneus · Freins · Éclairage · Niveaux',
      subtitle: 'Un véhicule bien entretenu = un véhicule sûr',
    },
    pastilles: [
      { tone: 'success', desc: 'Vérification périodique + avant chaque grand trajet.' },
      { tone: 'danger', desc: 'Pneus sous-gonflés = adhérence réduite.' },
      { tone: 'warning', desc: 'Un feu grillé = tu es moins vu = plus de risques.' },
    ],
  },

  'risques-comportements': {
    num: 12,
    title: 'Les risques et les bons comportements',
    subtitle: 'Adopter une conduite responsable',
    central: {
      title: 'Téléphone · Alcool · Fatigue · Passagers · Débridage',
      subtitle: 'Mes choix aujourd\'hui → ma sécurité demain',
    },
    pastilles: [
      { tone: 'success', desc: 'Je pose mon téléphone avant de démarrer.' },
      { tone: 'danger', desc: 'Alcool + drogues + médicaments = interdits.' },
      { tone: 'warning', desc: 'Je ne cherche pas à épater : je conduis pour arriver.' },
    ],
  },
};

export default VISUAL_CONFIGS;
